import { Grid } from '@mui/material';
import CancellationUnderageContent from 'cancellations/components/cancellation-underage';
import CancellationUnderageStepButtons from 'cancellations/components/cancellation-underage/buttons';
import { CancellationUnderageFormCrumbs } from 'cancellations/constants/cancellation-underage.crumbs';
import {
  transformAnprRequestDeath,
  transformAnprResponse,
} from 'cancellations/utils/transform-anpr-response-underage';
import { CancellationUnderage } from 'cancellations/view-models/cancellation-underage';
import { Form, Formik } from 'formik';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { OkResponse, ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import PmiTitle from 'shell/components/shared/title';
import ModalExit from 'cancellations/components/cancellation-underage/modal-exit';
import { cancellationValidationSchema } from './validation-schema';

interface CancellationUnderagePageProps {
  isWithoutBreadcrumbs?: boolean;
  disabledFromAct?: boolean;
  initialValues?: CancellationUnderage;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const CancellationUnderagePage = ({
  isWithoutBreadcrumbs,
  initialValues,
  onConfirm,
  onCancel,
}: CancellationUnderagePageProps): React.ReactElement => {
  const [queryParams] = useSearchParams();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const openCancelDialog = () => setIsCancelDialogOpen(true);
  const closeCancelDialog = () => setIsCancelDialogOpen(false);

  const navigate = useNavigate();

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const [anprResponse] = useHttpFetch<HttpResponse<ResponseAnprData>, OkResponse>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: (response) => response.data.payload?.risposta3002OK,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCancellation, saveCancellation] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/cancellazione-minore`, {
    onBootstrap: false,
    transformer: transformResponseSaveDataAnpr,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
  });

  const castedAnprData = responseCancellation.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const handleSubmit = useCallback(
    (form: CancellationUnderage) => {
      const data = transformAnprRequestDeath(form);

      (
        saveCancellation({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        toast.success(`Cancellazione di ${form.person?.fiscalCode} eseguita con successo`);

        if (onConfirm) {
          onConfirm();
          return;
        }

        navigate('/cancellazioni/minori');
      });
    },
    [saveCancellation, navigate, onConfirm]
  );

  const initialForm: CancellationUnderage | undefined = useMemo(() => {
    if (!anprResponse.data || anprResponse.isLoading || anprResponse.pristine) {
      return undefined;
    }
    return transformAnprResponse((anprResponse.data as OkResponse)?.listaSoggetti.datiSoggetto);
  }, [anprResponse.data, anprResponse.isLoading, anprResponse.pristine]);

  if (!initialForm) {
    return <></>;
  }

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : <Breadcrumbs items={CancellationUnderageFormCrumbs} />}
      <PmiTitle title="Cancellazione Minore" paddingBottom={1} />
      <div className="pmi-mt-2">
        <Formik
          initialValues={{ ...(initialForm || {}), ...(initialValues || {}) }}
          onSubmit={handleSubmit}
          validationSchema={cancellationValidationSchema}
        >
          <Form>
            <Grid container spacing={1}>
              <Grid item xs={12} md={2}>
                <CancellationUnderageStepButtons onCancel={openCancelDialog} />
              </Grid>
              <Grid item xs={12} md={10}>
                <div className="pmi-px-md-4">
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorMessage.title || ''}
                    messages={errorMessage.messages || []}
                    visibility={errorMessage.visibility || false}
                    onChangeVisibility={onVisibility}
                  />
                  <SearchMessages
                    type={MessageType.Warning}
                    title={
                      castedAnprData?.warnings?.length === 1
                        ? `C'è 1 warning`
                        : `Ci sono ${castedAnprData?.warnings?.length} warning`
                    }
                    messages={castedAnprData?.warnings || []}
                    visibility={
                      visibility.warning !== undefined
                        ? visibility.warning
                        : (castedAnprData?.warnings || [])?.length > 0
                    }
                    onChangeVisibility={onVisibility}
                  />
                  <CancellationUnderageContent />
                </div>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </div>
      <ModalExit
        onCancel={onCancel}
        isCancelDialogOpen={isCancelDialogOpen}
        closeCancelDialog={closeCancelDialog}
      />
    </Fragment>
  );
};

CancellationUnderagePage.displayName = 'Cancellation Underage page component';

export default CancellationUnderagePage;
